import axios from "axios";
import { getWallet, signPayload } from "./wallet";

// Backend URL resolution.
//
// REACT_APP_BACKEND_URL is baked into the bundle at BUILD time. To prevent
// the CharityCierge failure (production frontend calling the preview backend
// because the build inherited the preview .env), this module falls back to
// an empty string when the variable is unset or empty. Empty string + "/api"
// produces a RELATIVE URL, which the browser resolves against the page
// origin — so the production bundle calls https://labs.katlastechnology.com/api/*
// when served from that host, and the preview bundle calls the preview
// origin from its host. No preview hostname is ever baked into production JS
// unless REACT_APP_BACKEND_URL is explicitly set to one at production build
// time.
//
// Deployment policy:
//   Preview build:    REACT_APP_BACKEND_URL = https://<preview-host> (set in /app/frontend/.env)
//   Production build: REACT_APP_BACKEND_URL = https://labs.katlastechnology.com
//                     OR unset/empty (same-origin relative — recommended)
const RAW = process.env.REACT_APP_BACKEND_URL;
const BACKEND_URL = RAW && RAW.trim() ? RAW.trim().replace(/\/+$/, "") : "";
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
});

// ---------- helpers ----------
const toBase64 = (str) =>
  typeof window !== "undefined" && window.btoa
    ? window.btoa(str)
    : Buffer.from(str, "utf-8").toString("base64");

function canonicalPublicJwk(jwk) {
  // Same canonical ordering used to compute the fingerprint.
  return { crv: jwk.crv, kty: jwk.kty, x: jwk.x, y: jwk.y };
}

async function walletAuthHeaders() {
  const wallet = getWallet();
  if (!wallet) {
    throw new Error("WALLET_REQUIRED");
  }
  const { data } = await api.post("/auth/challenge", {
    wallet_fingerprint: wallet.fingerprint,
  });
  const nonce = data.nonce;
  const signature = await signPayload(nonce);
  const pubJwkJson = JSON.stringify(canonicalPublicJwk(wallet.publicJwk));
  return {
    "X-Wallet-Fingerprint": wallet.fingerprint,
    "X-Wallet-PublicJwk": toBase64(pubJwkJson),
    "X-Wallet-Nonce": nonce,
    "X-Wallet-Signature": signature,
  };
}

// ---------- public endpoints ----------
export async function saveDraft(payload) {
  const { data } = await api.post("/use-case-drafts", payload);
  return data;
}

export async function submitSandboxRequest(payload) {
  const { data } = await api.post("/sandbox-requests", payload);
  return data;
}

export async function submitContactRequest(payload) {
  const { data } = await api.post("/contact-requests", payload);
  return data;
}

// ---------- wallet-protected endpoints ----------
export async function listDraftsByWallet(fingerprint) {
  const headers = await walletAuthHeaders();
  const { data } = await api.get(`/use-case-drafts/by-wallet/${fingerprint}`, {
    headers,
  });
  return data;
}

export async function deleteDraft(id) {
  const headers = await walletAuthHeaders();
  const { data } = await api.delete(`/use-case-drafts/${id}`, { headers });
  return data;
}
